import React, { useEffect, useMemo } from 'react';
import { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    Skeleton,
    Avatar,
    Tooltip,
    Divider,
} from '@mui/material';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TaskApiService from '../../api/TaskApiService';
import { useRefetchTrigger } from '../../hooks/useRefetchTrigger';

const taskApiService = new TaskApiService();

const CATEGORIES = [
    {
        key: 'overdue',
        icon: EventBusyRoundedIcon,
        color: '#ef4444',
        iconBg: '#fee2e2',
        chipBg: '#fee2e2',
        chipText: '#dc2626',
        dateColor: '#ef4444',
    },
    {
        key: 'dueIn7Days',
        icon: AccessTimeRoundedIcon,
        color: '#f97316',
        iconBg: '#ffedd5',
        chipBg: '#ffedd5',
        chipText: '#ea580c',
        dateColor: '#f97316',
    },
    {
        key: 'dueIn30Days',
        icon: CalendarMonthRoundedIcon,
        color: '#eab308',
        iconBg: '#fef9c3',
        chipBg: '#fef9c3',
        chipText: '#a16207',
        dateColor: '#ca8a04',
    },
    {
        key: 'onTrack',
        icon: TaskAltRoundedIcon,
        color: '#22c55e',
        iconBg: '#dcfce7',
        chipBg: '#dcfce7',
        chipText: '#15803d',
        dateColor: '#6b7280',
    },
    {
        key: 'noDueDate',
        icon: CalendarMonthRoundedIcon,
        color: '#9ca3af',
        iconBg: '#f3f4f6',
        chipBg: '#f3f4f6',
        chipText: '#6b7280',
        dateColor: '#9ca3af',
    },
];

const catLabel = (t, key) => t(`dashboardTaskDueDate.cat.${key}.label`);
const catBarLabel = (t, key) => t(`dashboardTaskDueDate.cat.${key}.barLabel`);
const catChipLabel = (t, key) => t(`dashboardTaskDueDate.cat.${key}.chipLabel`);

// Show the 3 most urgent tasks in the preview (matches reference image)
const PREVIEW_LIMIT = 3;

function getInitials(username) {
    if (!username) return '?';
    const atIdx = username.indexOf('@');
    const local = atIdx > -1 ? username.substring(0, atIdx) : username;
    const parts = local.replace(/[._-]/g, ' ').split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return local.substring(0, 2).toUpperCase();
}

function formatDueDate(due, locale, naLabel) {
    if (!due) return naLabel;
    return new Date(due).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatToday(locale) {
    return new Date().toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ category, count, loading }) {
    const { t } = useTranslation();
    const Icon = category.icon;
    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.875,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                minWidth: 0,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 1.5,
                    backgroundColor: category.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ fontSize: 20, color: category.color }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                {loading ? (
                    <>
                        <Skeleton variant="text" width={28} height={28} />
                        <Skeleton variant="text" width={52} height={12} />
                        <Skeleton variant="text" width={40} height={11} />
                    </>
                ) : (
                    <>
                        <Typography variant="h6" fontWeight={800} lineHeight={1} color="text.primary">
                            {count}
                        </Typography>
                        <Typography variant="caption" fontWeight={600} color="text.primary" lineHeight={1.2} noWrap display="block">
                            {catLabel(t, category.key)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }} noWrap display="block">
                            {t('dashboardTaskDueDate.taskCount', { count })}
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    );
}

// ── Distribution Bar ──────────────────────────────────────────────────────────

function AgingBar({ summary, total, loading }) {
    const { t } = useTranslation();
    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Skeleton variant="rounded" height={30} sx={{ borderRadius: 1.5 }} />
                <Skeleton variant="text" width="100%" height={12} />
                <Skeleton variant="text" width="60%" height={12} />
            </Box>
        );
    }

    // Compute visible segments so first/last get rounded outer corners
    const visibleSegments = CATEGORIES
        .map((cat) => ({ cat, count: summary?.[cat.key]?.count ?? 0 }))
        .filter(({ count }) => count > 0);

    return (
        <Box>
            {/*
              pt: 1 gives 8px breathing room above so segments can
              scale upward on hover without being clipped.
              No overflow: hidden — segments need to escape the container.
            */}
            <Box sx={{
                pt: 1,            // space above for hover pop (transparent — no grey bar)
                boxSizing: 'content-box',
            }}>
            <Box sx={{
                display: 'flex',
                height: 30,
                gap: '1px',
                backgroundColor: '#d1d5db',
                borderRadius: 1.5,
            }}>
                {total === 0 ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', borderRadius: 1.5 }}>
                        <Typography variant="caption" color="text.disabled">{t('dashboardTaskDueDate.aging.noTasks')}</Typography>
                    </Box>
                ) : (
                    visibleSegments.map(({ cat, count }, i) => {
                        const pct = (count / total) * 100;
                        const isFirst = i === 0;
                        const isLast = i === visibleSegments.length - 1;
                        const br = isFirst && isLast ? '6px'
                            : isFirst  ? '6px 0 0 6px'
                            : isLast   ? '0 6px 6px 0'
                            : 0;
                        return (
                            <Tooltip key={cat.key} title={t('dashboardTaskDueDate.tooltip', { label: catLabel(t, cat.key), count, pct: Math.round(pct) })} placement="top">
                                <Box
                                    sx={{
                                        width: `${pct}%`,
                                        minWidth: 2,
                                        height: '100%',
                                        backgroundColor: cat.color,
                                        borderRadius: br,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        position: 'relative',
                                        transformOrigin: 'bottom center',
                                        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                                        cursor: 'default',
                                        '&:hover': {
                                            transform: 'scaleY(1.35)',
                                            boxShadow: `0 -6px 14px ${cat.color}88`,
                                            zIndex: 2,
                                        },
                                    }}
                                >
                                    {pct >= 12 && (
                                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.65rem', textShadow: '0 1px 2px rgba(0,0,0,0.25)' }}>
                                            {count} ({Math.round(pct)}%)
                                        </Typography>
                                    )}
                                </Box>
                            </Tooltip>
                        );
                    })
                )}
            </Box>
            </Box>

            {/* % tick marks */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                {['0%', '25%', '50%', '75%', '100%'].map((tick) => (
                    <Typography key={tick} sx={{ fontSize: '0.58rem', color: '#9ca3af' }}>{tick}</Typography>
                ))}
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'nowrap' }}>
                {CATEGORIES.map((cat) => (
                    <Box key={cat.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Box sx={{ width: 9, height: 9, borderRadius: 0.4, backgroundColor: cat.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.62rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                            {catBarLabel(t, cat.key)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Task Preview Row ──────────────────────────────────────────────────────────

const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b'];

function TaskPreviewRow({ task, index, category, onNavigate }) {
    const { t, i18n } = useTranslation();
    const initials = getInitials(task.assignedToUser);
    const avatarBg = AVATAR_COLORS[initials.charCodeAt(0) % AVATAR_COLORS.length];

    return (
        <Box
            onClick={() => onNavigate(`/Task/${task.id}`)}
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 100px 96px 76px 18px',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.6,
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f9fafb' },
            }}
        >
            {/* Task name with numbered badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Box sx={{
                    width: 20, height: 20, borderRadius: '50%',
                    backgroundColor: category.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.58rem', fontWeight: 700, lineHeight: 1 }}>
                        {index + 1}
                    </Typography>
                </Box>
                <Typography variant="caption" noWrap color="text.primary" fontWeight={500}>
                    {task.shortDescription}
                </Typography>
            </Box>

            {/* Owner */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Avatar sx={{ width: 20, height: 20, fontSize: '0.55rem', fontWeight: 700, backgroundColor: avatarBg, flexShrink: 0 }}>
                    {initials}
                </Avatar>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>
                    {task.assignedToUser ?? '—'}
                </Typography>
            </Box>

            {/* Due Date */}
            <Typography variant="caption" fontWeight={600} noWrap sx={{ color: category.dateColor, fontSize: '0.65rem' }}>
                {formatDueDate(task.due, i18n.language, t('dashboardTaskDueDate.naDate'))}
            </Typography>

            {/* Status chip */}
            <Chip
                label={catChipLabel(t, category.key)}
                size="small"
                sx={{
                    height: 16, fontSize: '0.58rem', fontWeight: 700,
                    backgroundColor: category.chipBg, color: category.chipText,
                    border: `1px solid ${category.color}44`,
                    '& .MuiChip-label': { px: 0.6, py: 0 },
                }}
            />

            {/* Open icon */}
            <OpenInNewRoundedIcon sx={{ fontSize: 12, color: '#d1d5db' }} />
        </Box>
    );
}

// ── Main Widget ───────────────────────────────────────────────────────────────

const parseSettings = (settings) => {
    try {
        if (!settings || settings === '{}') return {};
        return typeof settings === 'string' ? JSON.parse(settings) : settings;
    } catch {
        return {};
    }
};

const getExcludedStatusIds = (settings) => {
    if (!Object.prototype.hasOwnProperty.call(settings, 'excludedStatusIds')) return [9];
    return Array.isArray(settings.excludedStatusIds) ? settings.excludedStatusIds : [];
};

const DashboardTaskDueDateSummary = ({ jsonSettings }) => {
    const { t, i18n } = useTranslation();
    const refetchTrigger = useRefetchTrigger();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const excludedStatusIds = getExcludedStatusIds(parseSettings(jsonSettings));
        setLoading(true);
        taskApiService.getTaskDueDateSummary(excludedStatusIds)
            .then((data) => setSummary(data))
            .finally(() => setLoading(false));
    }, [jsonSettings, refetchTrigger]);

    const total = summary
        ? (summary.overdue?.count ?? 0) + (summary.dueIn7Days?.count ?? 0)
          + (summary.dueIn30Days?.count ?? 0) + (summary.onTrack?.count ?? 0)
          + (summary.noDueDate?.count ?? 0)
        : 0;

    // Combine tasks by priority (overdue → 7d → 30d → no due date); on-track excluded
    const previewTasks = useMemo(() => {
        if (!summary) return [];
        const result = [];
        const previewCats = CATEGORIES.filter(c => c.key !== 'onTrack');
        for (const cat of previewCats) {
            for (const task of summary[cat.key]?.tasks ?? []) {
                if (result.length >= PREVIEW_LIMIT) break;
                result.push({ ...task, _category: cat });
            }
            if (result.length >= PREVIEW_LIMIT) break;
        }
        return result;
    }, [summary]);

    const columnHeaders = [
        t('dashboardTaskDueDate.column.task'),
        t('dashboardTaskDueDate.column.owner'),
        t('dashboardTaskDueDate.column.dueDate'),
        t('dashboardTaskDueDate.column.status'),
        '',
    ];

    return (
        // overflow: hidden is the key — no scrollbar ever appears
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            p: 1.5,
            gap: 1.25,
            boxSizing: 'border-box',
        }}>

            {/* ── Title ── */}
            <Typography variant="subtitle2" fontWeight={700} color="text.primary" flexShrink={0} lineHeight={1.2}>
                {t('dashboardTaskDueDate.title')}
            </Typography>

            {/* ── Stat Cards ── */}
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                {CATEGORIES.map((cat) => (
                    <StatCard key={cat.key} category={cat} count={summary?.[cat.key]?.count ?? 0} loading={loading} />
                ))}
            </Box>

            {/* ── Task Aging Distribution ── */}
            <Box sx={{ flexShrink: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                    <Typography variant="caption" fontWeight={700} color="text.primary">
                        {t('dashboardTaskDueDate.aging.title')}
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                        {loading ? (
                            <>
                                <Skeleton variant="text" width={72} height={16} sx={{ ml: 'auto' }} />
                                <Skeleton variant="text" width={88} height={12} sx={{ ml: 'auto' }} />
                            </>
                        ) : (
                            <>
                                <Typography variant="caption" fontWeight={700} color="text.primary" display="block">
                                    {t('dashboardTaskDueDate.aging.totalTasks')}&nbsp;&nbsp;{total}
                                </Typography>
                                <Typography sx={{ fontSize: '0.6rem', color: '#9ca3af' }}>
                                    {t('dashboardTaskDueDate.aging.asOf', { date: formatToday(i18n.language) })}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
                <AgingBar summary={summary} total={total} loading={loading} />
            </Box>

            <Divider sx={{ flexShrink: 0 }} />

            {/* ── Task Preview — fills all remaining height, never scrolls ── */}
            <Box sx={{
                flex: 1,
                minHeight: 0,       // lets flex child shrink below its content height
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Preview header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexShrink: 0 }}>
                    <Typography variant="caption" fontWeight={700} color="text.primary">
                        {t('dashboardTaskDueDate.preview.title')}
                    </Typography>
                    <Box
                        onClick={() => navigate('/Tasks')}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer',
                            color: '#6366f1',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'inherit' }}>
                            {t('dashboardTaskDueDate.preview.viewAll')}
                        </Typography>
                        <ArrowForwardRoundedIcon sx={{ fontSize: 12, color: 'inherit' }} />
                    </Box>
                </Box>

                {/* Column headers */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 100px 96px 76px 18px',
                        gap: 0.75,
                        px: 1,
                        pb: 0.4,
                        borderBottom: '1px solid #f3f4f6',
                        flexShrink: 0,
                    }}
                >
                    {columnHeaders.map((h, idx) => (
                        <Typography key={idx} sx={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {h}
                        </Typography>
                    ))}
                </Box>

                {/* Rows — these fill remaining space and clip gracefully if needed */}
                <Box
                    sx={{
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        flex: 1,
                        minHeight: 0,
                        pr: 0.5,
                    }}
                >
                    {loading ? (
                        [0, 1, 2].map((i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.6 }}>
                                <Skeleton variant="circular" width={20} height={20} />
                                <Skeleton variant="text" sx={{ flex: 1 }} height={14} />
                                <Skeleton variant="text" width={80} height={14} />
                                <Skeleton variant="text" width={72} height={14} />
                                <Skeleton variant="rounded" width={56} height={16} />
                            </Box>
                        ))
                    ) : previewTasks.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 0.5 }}>
                            <TaskAltRoundedIcon sx={{ fontSize: 28, color: '#d1d5db' }} />
                            <Typography variant="caption" color="text.secondary">
                                {t('dashboardTaskDueDate.preview.empty')}
                            </Typography>
                        </Box>
                    ) : (
                        previewTasks.map((task, idx) => (
                            <TaskPreviewRow
                                key={task.id}
                                task={task}
                                index={idx}
                                category={task._category}
                                onNavigate={navigate}
                            />
                        ))
                    )}
                </Box>
            </Box>

        </Box>
    );
};

export default DashboardTaskDueDateSummary;
